<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SiteHeader from './components/layout/SiteHeader.vue'
import SiteFooter from './components/layout/SiteFooter.vue'

const route = useRoute()
const mainContent = ref<HTMLElement | null>(null)

watch(() => route.fullPath, async () => {
  await nextTick()
  mainContent.value?.focus({ preventScroll: true })
})
</script>

<template>
  <div class="app-wrapper">
    <a class="skip-link" href="#main-content">Skip to content</a>
    <div class="ambient" aria-hidden="true">
      <span class="ambient-orb orb-violet" />
      <span class="ambient-orb orb-cyan" />
      <span class="voxel-horizon" />
    </div>

    <SiteHeader />
    <main id="main-content" ref="mainContent" class="main-content" tabindex="-1">
      <RouterView v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>
    <SiteFooter />
  </div>
</template>

<style scoped>
.app-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: clip;
}

.main-content {
  position: relative;
  z-index: 1;
  flex: 1;
}

.main-content:focus {
  outline: none;
}

.skip-link {
  position: fixed;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 1000;
  padding: 0.65rem 0.9rem;
  border: 1px solid var(--color-secondary);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  background: var(--bg-secondary);
  transform: translateY(-160%);
  transition: transform var(--transition-fast);
}

.skip-link:focus {
  transform: translateY(0);
}

.ambient {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.ambient::before {
  position: absolute;
  inset: 0;
  content: '';
  opacity: 0.48;
  background-image:
    linear-gradient(rgba(188, 196, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(188, 196, 255, 0.025) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to bottom, #000, transparent 82%);
}

.ambient-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(100px);
  opacity: 0.16;
}

.orb-violet {
  top: 8vh;
  left: -10vw;
  width: min(560px, 60vw);
  aspect-ratio: 1;
  background: #7c3aed;
}

.orb-cyan {
  top: 46vh;
  right: -14vw;
  width: min(480px, 55vw);
  aspect-ratio: 1;
  background: #0891b2;
}

.voxel-horizon {
  position: absolute;
  right: -4vw;
  bottom: -3rem;
  width: min(720px, 70vw);
  height: 22vh;
  min-height: 150px;
  opacity: 0.16;
  background:
    linear-gradient(135deg, transparent 50%, rgba(167, 139, 250, 0.32) 50%) 0 100% / 64px 64px,
    linear-gradient(225deg, transparent 50%, rgba(103, 232, 249, 0.2) 50%) 32px 100% / 64px 64px;
  mask-image: linear-gradient(to top, #000 10%, transparent 92%);
}

.page-enter-active,
.page-leave-active {
  transition: opacity 180ms ease, transform 260ms var(--ease-out);
}

.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.page-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
}
</style>
